/**
 * Targeting Strategist — autonomous keyword + SERP targeting (dual SEO/AEO).
 *
 * Non-LLM execute stage: it drives its own multi-turn tool loop. Has an editable
 * prompt in blog-stages (showPromptEditor) but no per-stage S3 artifacts. This
 * panel renders the decision dossier written to targeting.strategy.
 */

import React from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import { StrategistOverridePanel } from './StrategistOverridePanel';
import { StrategistDialoguePanel } from './StrategistDialoguePanel';
import { StrategistAcceptBar } from './StrategistAcceptBar';
import { StrategistLiveActivity } from './StrategistLiveActivity';
import type { ArticleDocument, StrategyKeyword } from '../types';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold text-gray-700 mb-1">{title}</div>
      {children}
    </div>
  );
}

function ScoreBar({ label, value, color }: { label: string; value?: number | null; color: string }) {
  const v = typeof value === 'number' ? Math.max(0, Math.min(100, value)) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs text-gray-600">
        <span>{label}</span>
        <span className="font-semibold text-gray-800">{value ?? '—'}</span>
      </div>
      <div className="h-2 rounded bg-gray-100">
        <div className={`h-2 rounded ${color}`} style={{ width: `${v}%` }} />
      </div>
    </div>
  );
}

function kw(k?: StrategyKeyword | null): string {
  if (!k || !k.keyword) return '—';
  const bits = [k.volume != null ? `vol ${k.volume}` : null, k.kd != null ? `KD ${k.kd}` : null]
    .filter(Boolean).join(', ');
  return bits ? `${k.keyword} (${bits})` : k.keyword;
}

function ThesisRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div>
      <span className="text-gray-400">{label}:</span> <span className="text-gray-800">{value}</span>
    </div>
  );
}

function ThesisSummary() {
  const { document } = useArticleContext();
  const t = document?.thesis;
  if (!t?.candidate_name) {
    return <div className="text-xs text-gray-500">No thesis yet — set the candidate topic first.</div>;
  }
  return (
    <div className="border border-gray-200 rounded p-3 text-xs space-y-1 bg-gray-50">
      <div className="text-gray-500">
        The strategist feeds this thesis into its prompt, then explores live keyword/SERP/AEO data and decides.
        (Editorial direction isn't used here — it guides the later writing stages.)
      </div>
      <div><span className="text-gray-400">Topic:</span> <span className="text-gray-800 font-medium">{t.candidate_name}</span></div>
      <ThesisRow label="ICP" value={t.icp} />
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <ThesisRow label="Funnel" value={t.funnel_stage} />
        <ThesisRow label="Focus" value={t.focus} />
        <ThesisRow label="Pillar" value={t.content_pillar} />
      </div>
      <ThesisRow label="Business context" value={t.business_context} />
      <ThesisRow label="Directional guidance" value={t.directional_guidance} />
    </div>
  );
}

function renderOutput(doc: ArticleDocument) {
  const s = doc.targeting?.strategy;
  if (!s || !s.verdict) {
    return <div className="text-sm text-gray-500">No targeting decision yet. Run the strategist.</div>;
  }
  const proceed = s.verdict === 'proceed';
  const chosen = s.chosen || {};
  const rejection = s.rejection;
  const candidates = s.candidates || [];

  return (
    <div className="space-y-4">
      {/* Verdict */}
      <div className="flex items-center gap-2">
        <span className={`text-sm font-semibold px-3 py-1 rounded-full ${proceed ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
          {s.verdict}
        </span>
        {s.strategy && s.strategy !== 'reject' && (
          <span className="text-xs font-medium px-2 py-1 rounded bg-indigo-50 text-indigo-700">{s.strategy}</span>
        )}
      </div>

      {/* Dual scores (proceed) */}
      {proceed && (
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <ScoreBar label="SEO score" value={chosen.seo_score} color="bg-blue-500" />
          <ScoreBar label="AEO score" value={chosen.aeo_score} color="bg-purple-500" />
        </div>
      )}

      {/* Success thesis */}
      {s.success_thesis && (
        <Section title="Why this wins for Talaera">
          <p className="text-sm text-gray-700">{s.success_thesis}</p>
        </Section>
      )}

      {/* Accept & continue — the primary action on a proceed (shows what it will steer) */}
      <StrategistAcceptBar verdict={s.verdict} articleDirection={s.article_direction} />

      {/* Proceed details */}
      {proceed && (
        <>
          <Section title="Chosen keywords">
            <div className="text-sm text-gray-800">{kw(chosen.primary_keyword)}</div>
            {(chosen.secondary_keywords || []).length > 0 && (
              <div className="text-xs text-gray-600 mt-1">
                Secondary: {(chosen.secondary_keywords || []).map((k) => k.keyword).join(', ')}
              </div>
            )}
          </Section>
          {(chosen.target_ai_queries || []).length > 0 && (
            <Section title="Target AI queries (AEO)">
              <ul className="text-xs text-gray-600 list-disc pl-4 space-y-0.5">
                {(chosen.target_ai_queries || []).map((q, i) => <li key={i}>{q}</li>)}
              </ul>
            </Section>
          )}
          {(chosen.competitor_urls || []).length > 0 && (
            <Section title={`Competitor URLs (${(chosen.competitor_urls || []).length})`}>
              {(chosen.competitor_urls || []).map((u, i) => (
                <div key={i} className="text-xs text-gray-600">
                  {u.position != null && <span className="text-gray-400">#{u.position} </span>}{u.domain || u.url}
                </div>
              ))}
            </Section>
          )}
        </>
      )}

      {/* Rejection */}
      {!proceed && rejection && (
        <div className="border border-amber-200 bg-amber-50 rounded p-3 space-y-2">
          <div className="flex items-center gap-2">
            <div className="text-xs font-semibold text-amber-800">Why rejected</div>
            {rejection.confidence && <span className="text-xs text-amber-600">confidence: {rejection.confidence}</span>}
          </div>
          <ul className="text-xs text-amber-900 list-disc pl-4 space-y-1">
            {(rejection.reasons || []).map((r, i) => <li key={i}>{r}</li>)}
          </ul>
          {(rejection.suggested_alternatives || []).length > 0 && (
            <div>
              <div className="text-xs font-semibold text-amber-800 mt-1">Suggested alternatives</div>
              <ul className="text-xs text-amber-900 space-y-1 mt-1">
                {(rejection.suggested_alternatives || []).map((a, i) => (
                  <li key={i}><span className="font-medium">{a.angle}</span>{a.why ? ` — ${a.why}` : ''}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Human override — redirect the strategist on any verdict (reject or proceed) */}
      <StrategistOverridePanel
        candidates={candidates}
        verdict={s.verdict}
        chosenKeyword={chosen.primary_keyword?.keyword || undefined}
      />

      {/* Dialogue — push back and let the strategist reconsider (§19 Tier 2) */}
      <StrategistDialoguePanel dialogue={s.dialogue || []} verdict={s.verdict} />

      {/* Reasoning trace */}
      {s.reasoning_trace && (
        <details className="text-xs">
          <summary className="cursor-pointer text-gray-600 font-medium">Reasoning trace</summary>
          <p className="text-gray-600 mt-1 whitespace-pre-wrap">{s.reasoning_trace}</p>
        </details>
      )}

      {/* Candidates explored */}
      {candidates.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-gray-600 font-medium">Candidates explored ({candidates.length})</summary>
          <div className="mt-1 space-y-1">
            {candidates.map((c, i) => (
              <div key={i} className="border border-gray-200 rounded p-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-800">{c.keyword}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${c.verdict === 'chosen' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{c.verdict}</span>
                </div>
                <div className="text-gray-500">
                  {c.volume != null && <>vol {c.volume} </>}{c.kd != null && <>KD {c.kd} </>}
                  {c.seo_score != null && <>· SEO {c.seo_score} </>}{c.aeo_score != null && <>AEO {c.aeo_score}</>}
                </div>
                {c.note && <div className="text-gray-500 mt-0.5">{c.note}</div>}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

export function StrategistV2() {
  const { document } = useArticleContext();
  const hasThesis = Boolean(document?.thesis?.candidate_name);

  return (
    <StagePanel
      stageId="stage_targeting_strategist"
      title="Targeting Strategist"
      description="Autonomously explores live keyword/SERP/AEO data and decides the best target — proceed (dual SEO/AEO) or reject with alternatives. Replaces the manual keyword + SERP stages."
      showPromptEditor
      runDisabled={!hasThesis}
      runDisabledReason={!hasThesis ? 'Requires a thesis topic' : undefined}
      runLabel="Run Strategist →"
      renderOutput={renderOutput}
      renderLiveProgress={(progress, running) => <StrategistLiveActivity progress={progress} running={running} />}
    >
      <ThesisSummary />
    </StagePanel>
  );
}
